import React, { useState } from 'react'
import { Paper, Typography, IconButton, Box, useTheme, useMediaQuery, Skeleton } from '@mui/material'
import { ArrowBack } from '@mui/icons-material'
import { Treemap, ResponsiveContainer } from 'recharts'
import { cluster } from '../data'
import BotonPremium from '../ButtonPremium'
import CustomizedContent from './Content'

const TreemapChart: React.FC<{
  enabled: boolean
  loading: boolean
  title: string
  data: any[]
}> = ({ enabled, loading, title, data }) => {
  const [selectedCluster, setSelectedCluster] = useState<any>(null)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const dataRender = enabled ? data : cluster

  const handleBack = (e: React.MouseEvent) => {
    setSelectedCluster(null)
  }

  const treemapData = selectedCluster
    ? selectedCluster.termsList.map((term: any) => ({
        name: term.term,
        value: term.hits
      }))
    : dataRender?.map((cluster) => ({
        name: cluster.cluster,
        value: cluster.hits
      }))

  if (!treemapData && !loading) return null

  // Estilos compartidos del Paper
  const paperSx = {
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
    gap: '16px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #e0e0e0',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    ...(isMobile && { marginBottom: '4.5rem' })
  }

  if (loading) {
    return (
      <Paper elevation={0} sx={paperSx}>
        {/* Header skeleton */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Box>
            <Skeleton variant="text" width={180} height={24} />
          </Box>
        </Box>

        {/* Treemap skeleton: bloques que imitan los rectángulos */}
        <Box
          sx={{
            height: 500,
            border: '2px solid #e0e0e0',
            borderRadius: '4px',
            overflow: 'hidden',
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr',
            gridTemplateRows: '2fr 1fr',
            gap: '4px',
            padding: '4px',
            backgroundColor: '#f5f5f5'
          }}
        >
          <Skeleton variant="rectangular" sx={{ gridRow: '1 / 3', height: '100%', borderRadius: '4px' }} />
          <Skeleton variant="rectangular" sx={{ height: '100%', borderRadius: '4px' }} />
          <Skeleton variant="rectangular" sx={{ height: '100%', borderRadius: '4px' }} />
          <Skeleton variant="rectangular" sx={{ height: '100%', borderRadius: '4px' }} />
          <Skeleton variant="rectangular" sx={{ height: '100%', borderRadius: '4px' }} />
        </Box>
      </Paper>
    )
  }

  return (
    <Paper elevation={0} sx={paperSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {selectedCluster && (
          <IconButton
            onClick={handleBack}
            size="small"
            sx={{
              backgroundColor: '#f5f5f5',
              '&:hover': { backgroundColor: '#e0e0e0' }
            }}
          >
            <ArrowBack fontSize="small" />
          </IconButton>
        )}
        <Box>
          <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600, color: '#666' }}>
            {title}
          </Typography>
          {selectedCluster && (
            <Typography variant="body2" sx={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
              {selectedCluster.cluster} ({selectedCluster.hits} hits)
            </Typography>
          )}
        </Box>
      </Box>

      <div
        style={{
          height: 500,
          border: selectedCluster ? '2px solid #4CAF50' : '2px solid #2196F3',
          opacity: enabled ? 1 : 0.6,
          filter: enabled ? 'none' : 'blur(3px)',
          pointerEvents: enabled ? 'auto' : 'none'
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <Treemap
            data={treemapData}
            dataKey="value"
            aspectRatio={4 / 3}
            stroke="#fff"
            content={
              <CustomizedContent
                dataRender={dataRender}
                selectedCluster={selectedCluster}
                setSelectedCluster={setSelectedCluster}
                isMobile={isMobile}
                enabled={enabled}
              />
            }
            isAnimationActive={false}
          />
        </ResponsiveContainer>
      </div>

      {!enabled && <BotonPremium />}
    </Paper>
  )
}

export default TreemapChart
